/**
 * Frontend app version & cache-housekeeping helpers.
 *
 * - APP_VERSION: bump this for every release. On boot, we compare the stored
 *   version with this constant; if mismatched, we purge stale browser caches
 *   (Cache Storage + sessionStorage + selective localStorage keys) while
 *   preserving the auth token & user identity so the user does not have to
 *   re-log-in or manually clear browser history.
 */

export const APP_VERSION = "2026.02.13-weaver";
const VERSION_KEY = "app_version";

// Keys that should survive a version upgrade. Everything else in localStorage
// is treated as disposable.
const PRESERVE_LOCAL_KEYS = ["token", "user", "session_kicked"];

const safeRemove = (storage, key) => {
  try { storage.removeItem(key); } catch { /* ignore */ }
};

const purgeLocalStorageExcept = (preserveKeys) => {
  try {
    const all = Object.keys(localStorage);
    all.forEach((k) => {
      if (!preserveKeys.includes(k)) safeRemove(localStorage, k);
    });
  } catch { /* ignore */ }
};

const purgeSessionStorage = () => {
  try { sessionStorage.clear(); } catch { /* ignore */ }
};

const purgeCacheStorage = async () => {
  if (!("caches" in window)) return;
  try {
    const names = await caches.keys();
    await Promise.all(names.map((n) => caches.delete(n)));
  } catch { /* ignore */ }
};

/**
 * Runs once on app boot. If the stored APP_VERSION doesn't match the one in
 * this build, we purge ephemeral caches and update the stored marker.
 * The auth token is preserved so users stay logged in.
 */
export async function ensureAppVersion() {
  let stored = null;
  try { stored = localStorage.getItem(VERSION_KEY); } catch { /* ignore */ }

  if (stored === APP_VERSION) return false; // up to date

  // Mismatch: clean stale state.
  purgeLocalStorageExcept(PRESERVE_LOCAL_KEYS);
  purgeSessionStorage();
  await purgeCacheStorage();

  try { localStorage.setItem(VERSION_KEY, APP_VERSION); } catch { /* ignore */ }
  return true; // we did clean up
}
